import * as EventEmitter from 'eventemitter3'
import { ActionTypes, EventTypes } from './ActionTypes'
import createService, { IService, IVendor } from './createService'
import { Transaction } from './transaction'
import extractApis from './utils/extractApis'
import isPromise from './utils/isPromise'

const defaults = {
  pageDefaults: () => ({}),
  requestTimeout: 15 * 1000,
}

export interface IMetrics {
  vendors: IVendor[]
  enabled?: boolean
  debug?: boolean
  cancelOnNext?: boolean
}

export class Metrics extends EventEmitter.EventEmitter {
  public enabled: boolean
  public debug: boolean
  public cancelOnNext: boolean
  public vendors: IVendor[]
  public services: IService[]
  public apiList: string[]
  public transaction: Transaction
  public apiImpl: {
    track: (...args: any[]) => Promise<any>
    [key: string]: any
  }

  constructor(options: IMetrics) {
    super()
    this.vendors = options.vendors
    this.enabled = options.enabled !== false
    this.debug = !!options.debug
    this.cancelOnNext =
      options.cancelOnNext !== undefined ? !!options.cancelOnNext : true
    this.services = this.vendors.map(vendor => createService(vendor))
    this.apiList = extractApis(this.services.map(service => service.apis))
    this.transaction = new Transaction()
    this.apiImpl = this.apiList.reduce((impl: any, api: string) => {
      impl[api] = (type?: EventTypes, data?: any) =>
        this.prepareTrack(api, type, data)
      return impl
    }, {})
    Object.freeze(this.apiImpl)
  }

  get api() {
    return this.apiImpl
  }

  private callServices(type: ActionTypes, promise: Promise<any>) {
    return promise.then((params: any) => {
      params = params || []
      const results: any[] = []
      const services = this.services
      const requestTimeout = defaults.requestTimeout

      function isCompleted() {
        return results.length === services.length
      }

      function clearTimer(timer?: NodeJS.Timeout) {
        if (timer) {
          clearTimeout(timer)
          timer = undefined
        }
      }

      return new Promise(resolve => {
        function process(requestProcess: any, result: any) {
          if (requestProcess.isTimeout) {
            return
          }
          requestProcess.isTimeout = true
          clearTimer(requestProcess.timer)
          results.push(result)
          if (isCompleted()) {
            resolve(results)
          }
        }

        services.map(service => {
          const { apis, name } = service
          const apiExists = !!apis && !!apis[type]
          if (apiExists) {
            if (typeof apis[type] !== 'function') {
              console.warn(
                `'${type}'${name ? `(${name} Service)` : ''} is not a function`,
              )
            }
          }

          let eventName: string = params[0]
          let data = Object.assign({}, params[1])

          // if experiment only send to GA
          if (
            service.experimentTracking !== true &&
            !Object.values(EventTypes).includes(eventName as EventTypes)
          ) {
            return process(
              {},
              {
                name,
                params,
                status: 'skipped',
              },
            )
          }

          // if actions given but not this action
          if (service.events !== undefined) {
            const actionNames = service.events.map(x => x.toString())
            if (
              !actionNames.includes(eventName) &&
              eventName !== EventTypes.PAGE_VIEW_EVENT.toString()
            ) {
              return process(
                {},
                {
                  name,
                  params,
                  status: 'skipped',
                },
              )
            }
          }

          // convert data
          if (service.conversions !== undefined) {
            const convertObject = service.conversions.dataConversion.get(
              params[0],
            )
            data =
              convertObject && typeof convertObject === 'function'
                ? convertObject(data)
                : data
            if (service.conversions.nameConversion !== undefined) {
              eventName =
                service.conversions.nameConversion.get(params[0]) || eventName
            }
          }

          let requestPromise: any =
            apiExists && typeof apis[type] === 'function'
              ? apis[type](eventName, data)
              : undefined
          if (!isPromise(requestPromise)) {
            requestPromise = Promise.resolve(requestPromise)
          }
          requestPromise.isTimeout = false
          requestPromise.timer = setTimeout(
            process,
            requestTimeout,
            requestPromise,
            {
              name,
              params: [eventName, data],
              error: new Error(`Request time out after ${requestTimeout} ms.`),
              status: 'failure',
            },
          )

          return requestPromise
            .then((response: any) => ({
              name,
              params: [eventName, data],
              response,
              status: 'success',
            }))
            .catch((error: any) => ({
              name,
              params: [eventName, data],
              error,
              status: 'failure',
            }))
            .then((response: any) => process(requestPromise, response))
        })
      })
    })
  }

  private createTransaction(promise: Promise<any>) {
    const tId = this.transaction.current()
    const cancelOnNext = this.cancelOnNext
    this.transaction.set(tId, {
      promise,
      cancelOnNext,
    })
    return tId
  }

  private clearTransaction(tId: number) {
    this.transaction.remove(tId)
  }

  private doTrack(type: ActionTypes, promise: Promise<any>, tId: number) {
    promise = this.callServices(type, promise)
    const dispatchEvent = (status: string, response: any, error?: any) => {
      const eventFacade: any = {
        type,
        status,
      }
      if (response) {
        eventFacade.response = response
      } else if (error) {
        eventFacade.error = error
      }
      if (tId) {
        eventFacade.transactionId = tId
        this.clearTransaction(tId)
      }
      this.emit(type, eventFacade)
      if (this.debug) {
        console.log('track result', eventFacade)
      }
    }

    promise
      .then(response => {
        dispatchEvent(
          response.every((item: any) => item.status !== 'failure')
            ? 'success'
            : 'failure',
          response,
        )
      })
      .catch(error => {
        dispatchEvent('failure', null, error)
      })
  }

  private mergeWith(data: any) {
    return Object.assign({}, defaults.pageDefaults, data)
  }

  private addCancelHook(promise: Promise<any>) {
    const tId = this.transaction.create()
    return promise.then(data => {
      return this.transaction.get(tId).shouldCancel
        ? Promise.reject(new Error('Page view cancelled'))
        : data
    })
  }

  private addEventNameToPromise(
    eventType: EventTypes | null,
    promise: Promise<any>,
    shouldMerge: boolean,
  ) {
    return promise.then((data: any) => {
      data = [shouldMerge ? this.mergeWith(data) : data]
      data.unshift(eventType)
      return data
    })
  }

  private inspectArguments(type: ActionTypes, eventObj?: any, data?: any) {
    let shouldMerge = true
    if (type !== ActionTypes.PAGE_VIEW) {
      // don't merge `pageDefaults` with track params unless it's explicitly requested by the third argument.
      shouldMerge = false
    }

    // set default page view event name when missing
    let eventType: EventTypes | null = eventObj as EventTypes
    // let [eventName, params] = args;
    if (!data && typeof eventObj !== 'string') {
      data = eventObj
      eventType =
        type === ActionTypes.PAGE_VIEW
          ? EventTypes.PAGE_VIEW_EVENT
          : type === ActionTypes.IDENTIFY
          ? EventTypes.IDENTIFY
          : null
    }

    // make sure 'data' is a promise
    if (!isPromise(data)) {
      data = Promise.resolve(data)
    }

    // add cancel hook so that it can be cancelled(rejected) if the promise is still pending when the route changes.
    if (type === ActionTypes.PAGE_VIEW) {
      data = this.addCancelHook(data)
    }

    // PAGE_VIEW or TRACK should always have 'eventName'
    if (eventObj) {
      data = this.addEventNameToPromise(eventType, data, shouldMerge)
    }

    const returnArr = [type, data]
    if (type === ActionTypes.PAGE_VIEW) {
      returnArr.push(this.createTransaction(data))
    }

    return returnArr
  }

  private prepareTrack(typeName: string, event?: any, data?: any) {
    if (!this.enabled) {
      return
    }
    const type = typeName as ActionTypes
    const args = this.inspectArguments(type, event, data)
    this.doTrack(args[0], args[1], args[2])
  }
}

export default function createMetrics(options: IMetrics) {
  const metrics = new Metrics(options)
  return {
    get enabled() {
      return metrics.enabled
    },
    get api() {
      return metrics.api
    },
  }
}
